import type { MeasureInfo, Timeline } from '@corocanto/scoring';
import { type Channel, channelOf, type MyLine } from './mix';

export interface PlannedNote {
  channel: Channel;
  midi: number;
  /** Seconds from the start of the range. */
  time: number;
  duration: number;
}

export interface CountIn {
  /** Number of clicks. */
  clicks: number;
  /** Seconds between clicks. */
  interval: number;
  /** Clicks per bar; the first click of each bar is accented. */
  perBar: number;
}

/** Everything the engine needs to play a range of the timeline. */
export interface PlaybackPlan {
  /** Timeline measures of the range (inclusive). */
  firstMeasure: number;
  lastMeasure: number;
  /** The range in timeline seconds. Looping repeats exactly this span. */
  rangeStartSec: number;
  rangeEndSec: number;
  /** Where to start inside the range, in seconds from its start. */
  offsetSec: number;
  countIn: CountIn | null;
  notes: PlannedNote[];
}

export interface PlanOptions {
  firstMeasure: number;
  lastMeasure: number;
  /** Timeline measure to start from (inside the range); defaults to the range start. */
  startMeasure?: number;
  countInBars: number;
  /** The chorist's part and line: in a divided part, the other line plays on the "other" channel. */
  mine?: MyLine | null;
}

/**
 * Plan playback of timeline measures firstMeasure..lastMeasure. Notes are kept if they start
 * inside the range and are cut at its end. The count-in uses the time signature and tempo of
 * the measure where playback starts.
 */
export function planPlayback(timeline: Timeline, measures: readonly MeasureInfo[], options: PlanOptions): PlaybackPlan {
  const last = Math.min(Math.max(options.lastMeasure, 0), timeline.measures.length - 1);
  const first = Math.min(Math.max(options.firstMeasure, 0), last);
  const start = Math.min(Math.max(options.startMeasure ?? first, first), last);
  const firstM = timeline.measures[first];
  const lastM = timeline.measures[last];
  const startM = timeline.measures[start];
  if (!firstM || !lastM || !startM) throw new Error('Empty timeline');

  const rangeStartSec = firstM.startSec;
  const rangeEndSec = lastM.startSec + lastM.durationSec;
  const notes: PlannedNote[] = [];
  for (const n of timeline.notes) {
    if (n.startSec < rangeStartSec - 1e-9 || n.startSec >= rangeEndSec - 1e-9) continue;
    notes.push({
      channel: channelOf(n, options.mine),
      midi: n.midi,
      time: n.startSec - rangeStartSec,
      duration: Math.min(n.durationSec, rangeEndSec - n.startSec),
    });
  }

  let countIn: CountIn | null = null;
  if (options.countInBars > 0) {
    const ts = measures[startM.measureIndex]?.timeSignature ?? { beats: 4, beatType: 4 };
    const secPerQuarter = startM.durationBeats > 0 ? startM.durationSec / startM.durationBeats : 0.5;
    countIn = {
      clicks: ts.beats * options.countInBars,
      interval: secPerQuarter * (4 / ts.beatType),
      perBar: ts.beats,
    };
  }

  return {
    firstMeasure: first,
    lastMeasure: last,
    rangeStartSec,
    rangeEndSec,
    offsetSec: startM.startSec - rangeStartSec,
    countIn,
    notes,
  };
}

export function countInDuration(countIn: CountIn | null): number {
  return countIn ? countIn.clicks * countIn.interval : 0;
}

/** A metronome click, in seconds from the start of the range; the first beat of each bar is accented. */
export interface Click {
  time: number;
  accent: boolean;
}

/**
 * Metronome clicks for timeline measures first..last, one per beat of each bar's time signature
 * (a quarter in 4/4, an eighth in 6/8). A pickup bar gets only the beats it has.
 */
export function metronomeClicks(timeline: Timeline, measures: readonly MeasureInfo[], first: number, last: number): Click[] {
  const start = timeline.measures[first]?.startSec ?? 0;
  const clicks: Click[] = [];
  for (let i = first; i <= last; i++) {
    const m = timeline.measures[i];
    if (!m || m.durationBeats <= 0) continue;
    const ts = measures[m.measureIndex]?.timeSignature ?? { beats: 4, beatType: 4 };
    const beatQuarters = 4 / ts.beatType;
    const secPerQuarter = m.durationSec / m.durationBeats;
    const count = Math.max(1, Math.round(m.durationBeats / beatQuarters));
    for (let k = 0; k < count; k++) {
      clicks.push({ time: m.startSec - start + k * beatQuarters * secPerQuarter, accent: k === 0 });
    }
  }
  return clicks;
}
